import React, { PureComponent } from 'react';
import { Trans, t } from '@lingui/macro';
import Image from 'next/image';
import cn from 'classnames';
import Link from './Link';
import favIcon from '../images/favicon.png';
import AllSystemPopup from './AllSystemPopup';
import { withRouter } from 'next/router'

import { genYYYYMM, YYYYMMtoYearMonth, format } from '../utils';
import { mixpanelTrack } from '../utils/track';
import {
    aboutUrl,
    activityUrl,
    overviewUrl,
    odUrl,
    genUrlBasedOnPage,
} from '../utils/urls';
import { OVERVIEW_PAGE, ACTIVITY_PAGE, OD_PAGE, defaultLocale } from '../utils/static';
import styles from './LayoutViz.module.scss';

class Layout extends PureComponent {
    constructor(props) {
        super(props);
        const { config } = props;
        const title = t({ id: 'nav.title' })
            .replace('[system]', config.name);
        this.state = {
            title,
            modalIsOpen: false,
            mobileCollapseIsOpen: false,
        };
    }

    handlePageTrack = (page) => {
        mixpanelTrack({ category: page, action: null, ref: 'navLink' });
    }

    changeAnalysisRange = (e) => {
        const { page, system, locale, router } = this.props;
        const { value } = e.target;
        const { year, month } = YYYYMMtoYearMonth(value);
        const link = genUrlBasedOnPage({
            page, system, year, month, locale,
        });
        if (page === ACTIVITY_PAGE || page === OD_PAGE) {
            location.assign(link);
        } else {
            router.push(link);
        }
    }

    onChange = (e) => {
        const {
            locale, system, page, year, month, router
        } = this.props;
        const changeLang = e.target.value;
        if (locale !== changeLang) {
            // use default link since next.js can redirect to the locale link by the locale value
            const link = genUrlBasedOnPage({
                page, system, year, month, locale: defaultLocale,
            });
            mixpanelTrack('Change Language', { ref: 'navLink', action: changeLang });
            router.push(link, link, {
                locale: changeLang
            });
        }
    }

    toggelMobileCollapse = () => {
        const { mobileCollapseIsOpen } = this.state;
        this.setState({ mobileCollapseIsOpen: !mobileCollapseIsOpen });
    }

    openModal = () => {
        mixpanelTrack('Show All Systems', { ref: 'navLink' });
        this.setState({ modalIsOpen: true });
    }

    closeModal = () => {
        this.setState({ modalIsOpen: false });
    }

    render() {
        const {
            system, locale, children, year, month, page, systemList, availableAnalysis,
        } = this.props;
        const { title, mobileCollapseIsOpen } = this.state;

        return (
            <div>
                <nav className={cn('navbar navbar-expand-lg navbar-light bg-light flex-row-reverse flex-sm-row', styles.navbar)} id="navbar">
                    <button
                        className="navbar-toggler"
                        type="button"
                        onClick={this.toggelMobileCollapse}
                        data-toggle="collapse"
                        data-target="#navbarTogglerDemo01"
                        aria-controls="navbarTogglerDemo01"
                        aria-expanded="false"
                        aria-label="Toggle navigation"
                    >
                        <span className="navbar-toggler-icon" />
                    </button>
                    <div className={cn('nav-title capitalize', styles.navTitle)}>
                        <Link href="/">
                            <a
                                className="navbar-brand d-none d-md-inline-block"
                            >
                                <Image src={favIcon} alt="Bike Visualization Logo" width={25} height={25} />
                            </a>
                        </Link>
                        <select
                            name="available-analysis"
                            id=""
                            className={cn('d-none d-md-inline-block custom-select custom-select-sm', styles.analysisSelect)}
                            value={genYYYYMM(year, month)}
                            onChange={this.changeAnalysisRange}
                        >
                            {availableAnalysis.map((item) => (
                                <optgroup key={item.year} label={item.year}>
                                    <option value={genYYYYMM(item.year)}>{item.year}</option>
                                    {item.month.map((mon) => (
                                        <option key={mon} value={genYYYYMM(item.year, mon)}>
                                            {format({ date: new Date(item.year, mon - 1), locale })}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        <h1 className={styles.title}>{title}</h1>
                    </div>
                    <div className={cn('collapse navbar-collapse', { show: mobileCollapseIsOpen })}>
                        <ul className={cn('navbar-nav mr-auto', styles.navbarNav)}>
                            <li className="nav-item">
                                <Link
                                    href={overviewUrl({
                                        system, year, month,
                                    })}
                                    onClick={() => this.handlePageTrack('System')}
                                >
                                    <a
                                        className={cn('nav-link', styles.navLink, styles.navLinkMobile, { [styles.active]: page === OVERVIEW_PAGE })}
                                    >
                                        <Trans id="nav.system" />
                                    </a>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link
                                    href={activityUrl({
                                        system, year, month,
                                    })}
                                    onClick={() => this.handlePageTrack('Activity')}
                                >
                                    <a
                                        className={cn('nav-link', styles.navLink, styles.navLinkMobile, { [styles.active]: page === ACTIVITY_PAGE })}

                                    >
                                        <Trans id="nav.heatmap" />
                                    </a>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link
                                    href={odUrl({
                                        system, year, month,
                                    })}
                                    onClick={() => this.handlePageTrack('Od')}
                                >
                                    <a
                                        className={cn('nav-link', styles.navLink, styles.navLinkMobile, { [styles.active]: page === OD_PAGE })}
                                    >
                                        <Trans id="nav.od" />
                                    </a>
                                </Link>
                            </li>
                            <li className={cn('nav-item d-block d-md-none')}>
                                <Link
                                    href={aboutUrl()}
                                    onClick={() => this.handlePageTrack('About')}
                                >
                                    <a
                                        className={cn('nav-link', styles.navLink, styles.navLinkMobile)}
                                    >
                                        <Trans id="nav.about" />
                                    </a>
                                </Link>
                            </li>
                        </ul>
                        <ul className="nav navbar-nav navbar-right d-none d-md-flex">
                            <li className={cn('nav-item', styles.navLinkMobile)}>
                                <Link
                                    href={aboutUrl()}
                                    onClick={() => this.handlePageTrack('About')}
                                >
                                    <a
                                        className="nav-link"
                                    >
                                        <Trans id="nav.about" />
                                    </a>
                                </Link>
                            </li>
                            <li className={cn('nav-item d-flex align-items-center', styles.navLinkMobile)}>
                                <select className="custom-select custom-select-sm" value={locale} onChange={this.onChange}>
                                    <option value="en">English</option>
                                    <option value="zh-hant">繁體中文</option>
                                    <option value="zh-hans">简体中文</option>
                                </select>
                            </li>
                            <li className={cn('nav-item', styles.navLinkMobile)}>
                                <button type="button" className="btn btn-link nav-link showAll d-none d-md-block" onClick={this.openModal}>
                                    <i className="fa fa-list fa-lg" />
                                </button>
                            </li>
                        </ul>
                    </div>
                </nav>
                {children}
                {this.state.modalIsOpen
                    ? (
                        <AllSystemPopup
                            open={this.state.modalIsOpen}
                            locale={locale}
                            close={this.closeModal}
                            systemList={systemList}
                        />
                    )
                    : null}
            </div>
        );
    }
}

export default withRouter(Layout);
